import React, { useState, useEffect, useRef } from 'react';
import './ResumeEditor.css';
import html2canvas from 'html2canvas';
import ProjectsEditor from './ProjectsEditor';
import { jsPDF } from 'jspdf';
import { SkillsEditor } from "./SkillsEditor";
import { ProfileImageUploader } from './ProfileImageUploader';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import SubscriptionModal from './SubscriptionModal'; 
import { checkUserSubscription } from './checkUserSubscription'
import OneTimePaymentModal from './OneTimePaymentModal'; // Import your Stripe modal component
  import { 
    updateContactField, 
    updateSummaryField, 
    updateListSection,
    updateWorkExperienceSection, 
    updateEducationSection,
    updateFont,
    updateFontSize,
    updateThemeColor,
    updateSimpleStringSection,
    updateCertificationsSection,
    updateProjectsSection,
    updateSkillsSection
  } from './ResumeEditorHelpers';
  import { handleCheckout } from './checkoutUtils';

const AI_SECTIONS = [
  "contact_information", "summary", "work_experience", "education", "skills", "projects", 
  "awards_and_achievements", "research_projects", "presentations", "languages", "certifications", "hobbies"
];

const mainSections = ["contact_information", "summary", "work_experience", "education", "skills"];

const sidebarSections = [
  "contact_information", "skills", "awards_and_achievements", "languages", "certifications", "hobbies", "presentations", "research_projects"];


const ResumeEditor = ({ initialResume, resumeData, templateId }) => {
  const [resumeHtml, setResumeHtml] = useState(initialResume);
  const [additionalSections, setAdditionalSections] = useState([]);
  const [existingSections, setExistingSections] = useState([]);
  const [availableSections, setAvailableSections] = useState([]);
  const [selectedFont, setSelectedFont] = useState('Arial');
  const [fontSize, setFontSize] = useState('16px');
  const [themeColor, setThemeColor] = useState(null);
  const [expandedSections, setExpandedSections] = useState({});
  const [history, setHistory] = useState([]);
  // Store edited data in a structured object
  const [editedData, setEditedData] = useState({});
  const iframeRef = useRef(null);
  const hasSidebar = [2, 7, 10, 11].includes(templateId);
  const [previewImages, setPreviewImages] = useState([]);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [hasPaid, setHasPaid] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [user, setUser] = useState(null);
  const [isSignupOpen, setIsSignupOpen] = useState(false);


  // State for PDF preview modal
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const navigate = useNavigate();

  // On mount, retrieve any saved resume state. 
  useEffect(() => {
     const saved = localStorage.getItem('resumeHtml');
      if (saved) 
        { setResumeHtml(saved); } 
    }, []);

    // Whenever resumeHtml updates, save it to local storage. 
    useEffect(() => {
      localStorage.setItem('resumeHtml', resumeHtml); 
      }, [resumeHtml]);

  // ----------------- IFRAME PREVIEW -----------------
  useEffect(() => {
    if (!iframeRef.current) return;
    const iframeDoc = iframeRef.current.contentDocument;
    iframeDoc.open();
    iframeDoc.write(resumeHtml);
    iframeDoc.close();
    iframeDoc.body.style.userSelect = "none";
    iframeDoc.body.style.pointerEvents = "none";
  }, [resumeHtml]);

    // Listen for authenticated user, and if present, run the subscription check.
    useEffect(() => {
      const auth = getAuth();
      const unsubscribe = auth.onAuthStateChanged(async (u) => {
        setUser(u);
        if (u) {
          const isActive = await checkUserSubscription(u.uid);
          console.log(`Subscription Active for user ${u.uid}:`, isActive);
          setIsSubscribed(isActive);
        } else {
          setIsSubscribed(false);
        }
      });
  
      return () => unsubscribe();
    }, []);
    
    useEffect(() => {
      if (resumeData) {
        setEditedData(resumeData);
      }
    }, [resumeData]);
    
  

  // ----------------- FONT & THEME -----------------
  useEffect(() => { applyTextStyles(); }, [selectedFont, fontSize]);
  const applyTextStyles = () => {
    if (iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument;
      const styleTag = iframeDoc.createElement('style');
      styleTag.innerHTML = `body { font-family: ${selectedFont} !important; font-size: ${fontSize} !important; }`;
      iframeDoc.head.appendChild(styleTag);
    }
  };

  useEffect(() => {
    if (iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument;
      const oldStyleTag = iframeDoc.head.querySelector("#theme-style");
      if (oldStyleTag) oldStyleTag.remove();
      const styleTag = iframeDoc.createElement("style");
      styleTag.id = "theme-style";
      styleTag.innerHTML = `
        #sidebar { background-color: ${themeColor} !important; }
        #content h3 { color: ${themeColor} !important; }
      `;
      iframeDoc.head.appendChild(styleTag);
    }
  }, [themeColor]);

  useEffect(() => {
    if (iframeRef.current) {
      const iframeDoc = iframeRef.current.contentDocument;
      const resumeContainer = iframeDoc.querySelector(".resume-container");
      if (resumeContainer) {
        resumeContainer.style.height = "auto";
        resumeContainer.style.minHeight = `${resumeContainer.scrollHeight}px`;
      }
    }
  }, [resumeHtml]);

      // Dynamically detect sections already in the resume
      useEffect(() => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(resumeHtml, "text/html");
        
        const existingSections = AI_SECTIONS.filter(section => doc.querySelector(`#${section}`));
        const missingSections = AI_SECTIONS.filter(section => !existingSections.includes(section));

        setAvailableSections(missingSections);
    }, [resumeHtml]); // ✅ Runs every time resumeHtml changes

  // ----------------- UPDATE FUNCTIONS -----------------
  // For updating font:
  const handleUpdateFont = (newFont) => {
    saveToHistory();
    setSelectedFont(newFont);
    const updatedHtml = updateFont(resumeHtml, newFont);
    setResumeHtml(updatedHtml);
  };

  // For updating font size:
  const handleUpdateFontSize = (newSize) => {
    saveToHistory();
    setFontSize(newSize);
    const updatedHtml = updateFontSize(resumeHtml, newSize);
    setResumeHtml(updatedHtml);
  };

  const handleUpdateThemeColor = (newColor) => {
    saveToHistory(); // if you have history tracking
    setThemeColor(newColor);
    // Update both the live document and the resumeHTML string.
    const updatedHtml = updateThemeColor(newColor, resumeHtml);
    setResumeHtml(updatedHtml);
  };
  

  // Save state history
  const saveToHistory = () => {
    setHistory(prevHistory => [
      ...prevHistory,
      { resumeHtml, selectedFont, fontSize, themeColor, additionalSections: [...additionalSections], editedData: JSON.parse(JSON.stringify(editedData)) }
    ]);
  };

  const undoLastChange = () => {
    if (history.length > 0) {
      const lastState = history[history.length - 1];
      setResumeHtml(lastState.resumeHtml);
      setSelectedFont(lastState.selectedFont);
      setFontSize(lastState.fontSize);
      setThemeColor(lastState.themeColor);
      setAdditionalSections(lastState.additionalSections);
      setEditedData(lastState.editedData);
      setHistory(history.slice(0, -1));
    }
  };

  // ----------------- SECTION EDITORS -----------------

  // Render granular editor for contact_information.
  const renderContactInfoEditor = () => {
    const contact = editedData.contact_information || {};
    const fallback = (resumeData && resumeData.contact_information) || {};
  
    // A helper function to update a given field
    const handleUpdate = (field) => {
      saveToHistory();
      // Determine the new value from the editedData; fallback if not present.
      const newValue = contact[field] || fallback[field] || "";
      // Call the helper and update resumeHtml state
      const updatedHtml = updateContactField(resumeHtml, field, newValue);
      setResumeHtml(updatedHtml);
    };
  
    return (
      <div className="contact-info-editor">
        <h3>Edit Contact Information</h3>
        <div>
          <label>Name</label>
          <input
            type="text"
            value={contact.name || fallback.name || ""}
            onChange={(e) => handleContactInfoChange("name", e.target.value)}
          />
          <button onClick={() => handleUpdate("name")}>Update Name</button>
        </div>
        <div>
          <label>Email</label>
          <input
            type="text"
            value={contact.email || fallback.email || ""}
            onChange={(e) => handleContactInfoChange("email", e.target.value)}
          />
          <button onClick={() => handleUpdate("email")}>Update Email</button>
        </div>
        <div>
          <label>Phone</label>
          <input
            type="text"
            value={contact.phone || fallback.phone || ""}
            onChange={(e) => handleContactInfoChange("phone", e.target.value)}
          />
          <button onClick={() => handleUpdate("phone")}>Update Phone</button>
        </div>
        <div>
          <label>Location</label>
          <input
            type="text"
            value={contact.location || fallback.location || ""}
            onChange={(e) => handleContactInfoChange("location", e.target.value)}
          />
          <button onClick={() => handleUpdate("location")}>Update Location</button>
        </div>
        <div>
          <label>LinkedIn</label>
          <input
            type="text"
            value={contact.linkedIn || fallback.linkedIn || ""}
            onChange={(e) => handleContactInfoChange("linkedIn", e.target.value)}
          />
          <button onClick={() => handleUpdate("linkedIn")}>Update LinkedIn</button>
        </div>
        <div>
          <label>Website</label>
          <input
            type="text"
            value={contact.website || fallback.website || ""}
            onChange={(e) => handleContactInfoChange("website", e.target.value)}
          />
          <button onClick={() => handleUpdate("website")}>Update Website</button>
        </div>
        {hasSidebar && (
          <button onClick={() => addSectionToSidebar("contact_information")}>
            Add to Sidebar
          </button>
        )}
        <button onClick={() => removeSection("contact_information")}>
          Remove
        </button>
      </div>
    );
  };
  

// Then, in your UI editor for Work Experience:
const renderWorkExperienceEditor = () => {
  const experiences = editedData.work_experience || (resumeData && resumeData.work_experience) || [];
  return (
    <div className="work-experience-editor">
      <h3>Edit Work Experience</h3>
      {experiences.map((exp, idx) => (
        <div key={idx} style={{ border: '1px solid #ccc', padding: '10px', marginBottom: '10px' }}>
          <label>Title</label>
          <input
            type="text"
            value={exp.title}
            onChange={(e) => {
              const newExp = { ...exp, title: e.target.value };
              const updated = [...experiences];
              updated[idx] = newExp;
              setEditedData(prev => ({ ...prev, work_experience: updated }));
            }}
          />
          <label>Company</label>
          <input
            type="text"
            value={exp.company}
            onChange={(e) => {
              const newExp = { ...exp, company: e.target.value };
              const updated = [...experiences];
              updated[idx] = newExp;
              setEditedData(prev => ({ ...prev, work_experience: updated }));
            }}
          />
          <label>Start Date</label>
          <input
            type="date"
            value={exp.startDate}
            onChange={(e) => {
              const newExp = { ...exp, startDate: e.target.value };
              const updated = [...experiences];
              updated[idx] = newExp;
              setEditedData(prev => ({ ...prev, work_experience: updated }));
            }}
          />
          <label>End Date</label>
          <input
            type="date"
            value={exp.endDate}
            onChange={(e) => {
              const newExp = { ...exp, endDate: e.target.value };
              const updated = [...experiences];
              updated[idx] = newExp;
              setEditedData(prev => ({ ...prev, work_experience: updated }));
            }}
          />
          <label>Location</label>
          <input
            type="text"
            value={exp.location}
            onChange={(e) => {
              const newExp = { ...exp, location: e.target.value };
              const updated = [...experiences];
              updated[idx] = newExp;
              setEditedData(prev => ({ ...prev, work_experience: updated }));
            }}
          />
          <label>Responsibilities (one per line)</label>
          <textarea
            value={Array.isArray(exp.responsibilities) ? exp.responsibilities.join("\n") : exp.responsibilities}
            onChange={(e) => {
              const newExp = { ...exp, responsibilities: e.target.value.split("\n") };
              const updated = [...experiences];
              updated[idx] = newExp;
              setEditedData(prev => ({ ...prev, work_experience: updated }));
            }}
            style={{ whiteSpace: 'pre-wrap' }}
          />
          {/* Delete Experience button shows only if there's more than one entry */}
          {experiences.length > 1 && (
            <button onClick={() => {
              saveToHistory();
              const updated = experiences.filter((_, index) => index !== idx);
              setEditedData(prev => ({ ...prev, work_experience: updated }));
            }}>
              Delete Experience
            </button>
          )}
        </div>
      ))}
      <button onClick={() => {
        saveToHistory();
        const newEntry = { title: "", company: "", startDate: "", endDate: "", location: "", responsibilities: [] };
        const updated = [...experiences, newEntry];
        setEditedData(prev => ({ ...prev, work_experience: updated }));
      }}>
        Add New Experience
      </button>
      <button onClick={() => {
        const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
        updateWorkExperienceSection(doc, editedData.work_experience || experiences);
        setResumeHtml(doc.documentElement.outerHTML);
      }}>
        Update Work Experience
      </button>
    </div>
  );
};


// Similarly, create renderEducationEditor using updateEducationSection
function renderEducationEditor() {
  const educations = editedData.education || (resumeData && resumeData.education) || [];
  return (
    <div className="education-editor">
      <h3>Edit Education</h3>
      {educations.map((edu, idx) => (
        <div key={idx} style={{ border: '1px solid #ccc', padding: '10px', marginBottom: '10px' }}>
          <label>Degree</label>
          <input
            type="text"
            value={edu.degree}
            onChange={(e) => {
              const newEdu = { ...edu, degree: e.target.value };
              const updated = [...educations];
              updated[idx] = newEdu;
              setEditedData(prev => ({ ...prev, education: updated }));
            }}
          />
          <label>Institution</label>
          <input
            type="text"
            value={edu.institution}
            onChange={(e) => {
              const newEdu = { ...edu, institution: e.target.value };
              const updated = [...educations];
              updated[idx] = newEdu;
              setEditedData(prev => ({ ...prev, education: updated }));
            }}
          />
          <label>Graduation Year</label>
          <input
            type="text" // or type="number" if you prefer numeric input
            value={edu.year || ""}
            onChange={(e) => {
              const newEdu = { ...edu, year: e.target.value };
              const updated = [...educations];
              updated[idx] = newEdu;
              setEditedData(prev => ({ ...prev, education: updated }));
            }}
          />
          {educations.length > 1 && (
            <button onClick={() => {
              saveToHistory();
              const updated = educations.filter((_, index) => index !== idx);
              setEditedData(prev => ({ ...prev, education: updated }));
            }}>
              Delete Education
            </button>
          )}
        </div>
      ))}
      <button onClick={() => {
        saveToHistory();
        // Create a new entry with only degree, institution, and year.
        const newEntry = { degree: "", institution: "", year: "" };
        const updated = [...educations, newEntry];
        setEditedData(prev => ({ ...prev, education: updated }));
      }}>
        Add New Education
      </button>
      <button onClick={() => {
        const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
        updateEducationSection(doc, editedData.education || educations);
        setResumeHtml(doc.documentElement.outerHTML);
      }}>
        Update Education
      </button>
    </div>
  );
}

function HobbiesEditor({ resumeHtml, editedData, setEditedData, setResumeHtml }) {
  const rawHobbies = editedData.hobbies || (resumeData && resumeData.hobbies) || [];
  const [hobbiesText, setHobbiesText] = useState(rawHobbies.join("\n"));

  const handleUpdateHobbies = () => {
    saveToHistory();
    const newArr = hobbiesText.split("\n").map(line => line.trim()).filter(Boolean);
    // If your <ul> is class="hobbies-list", pass that in:
    const updatedHtml = updateListSection(resumeHtml, "hobbies", newArr, "hobbies-list");
    setResumeHtml(updatedHtml);
    setEditedData(prev => ({ ...prev, hobbies: newArr }));
  };

  return (
    <div>
      <h3>Edit Hobbies</h3>
      <textarea
        value={hobbiesText}
        onChange={(e) => setHobbiesText(e.target.value)}
        style={{ whiteSpace: 'pre-wrap' }}
      />
      <button onClick={handleUpdateHobbies}>Update Hobbies</button>
    </div>
  );
}

function AwardsEditor({ resumeHtml, editedData, setEditedData, setResumeHtml }) {
  const rawAwards = editedData.awards_and_achievements || (resumeData && resumeData.awards_and_achievements) || [];
  const [awardsText, setAwardsText] = useState(rawAwards.join("\n"));

  const handleUpdateAwards = () => {
    saveToHistory();
    const newArr = awardsText.split("\n").map(line => line.trim()).filter(Boolean);
    const updatedHtml = updateListSection(resumeHtml, "awards_and_achievements", newArr, "skills-list");
    setResumeHtml(updatedHtml);
    setEditedData(prev => ({ ...prev, awards_and_achievements: newArr }));
  };

  return (
    <div>
      <h3>Edit Awards & Achievements</h3>
      <textarea
        value={awardsText}
        onChange={(e) => setAwardsText(e.target.value)}
        style={{ whiteSpace: 'pre-wrap' }}
      />
      <button onClick={handleUpdateAwards}>Update Awards</button>
    </div>
  );
}

function LanguagesEditor({ resumeHtml, editedData, setEditedData, setResumeHtml }) {
  const rawLangs = editedData.languages || (resumeData && resumeData.languages) || [];
  const [langText, setLangText] = useState(rawLangs.join("\n"));

  const handleUpdateLanguages = () => {
    saveToHistory();
    const newArr = langText.split("\n").map(line => line.trim()).filter(Boolean);
    const updatedHtml = updateListSection(resumeHtml, "languages", newArr, "skills-list");
    setResumeHtml(updatedHtml);
    setEditedData(prev => ({ ...prev, languages: newArr }));
  };

  return (
    <div>
      <h3>Edit Languages</h3>
      <textarea
        value={langText}
        onChange={(e) => setLangText(e.target.value)}
        style={{ whiteSpace: 'pre-wrap' }}
      />
      <button onClick={handleUpdateLanguages}>Update Languages</button>
    </div>
  );
}


function PresentationsEditor({ resumeHtml, editedData, setEditedData, setResumeHtml }) {
  const rawPres = editedData.presentations || (resumeData && resumeData.presentations) || [];
  const [presText, setPresText] = useState(rawPres.join("\n"));

  const handleUpdatePresentations = () => {
    saveToHistory();
    const newArr = presText.split("\n").map(line => line.trim()).filter(Boolean);
    const updatedHtml = updateListSection(resumeHtml, "presentations", newArr, "skills-list");
    setResumeHtml(updatedHtml);
    setEditedData(prev => ({ ...prev, presentations: newArr }));
  };

  return (
    <div>
      <h3>Edit Presentations</h3>
      <textarea
        value={presText}
        onChange={(e) => setPresText(e.target.value)}
        style={{ whiteSpace: 'pre-wrap' }}
      />
      <button onClick={handleUpdatePresentations}>Update Presentations</button>
    </div>
  );
}

function ResearchProjectsEditor({ resumeHtml, editedData, setEditedData, setResumeHtml }) {
  const rawRes = editedData.research_projects || (resumeData && resumeData.research_projects) || [];
  const [resText, setResText] = useState(rawRes.join("\n"));

  const handleUpdateResearch = () => {
    saveToHistory();
    const newArr = resText.split("\n").map(line => line.trim()).filter(Boolean);
    const updatedHtml = updateListSection(resumeHtml, "research_projects", newArr, "skills-list");
    setResumeHtml(updatedHtml);
    setEditedData(prev => ({ ...prev, research_projects: newArr }));
  };

  return (
    <div>
      <h3>Edit Research Projects</h3>
      <textarea
        value={resText}
        onChange={(e) => setResText(e.target.value)}
        style={{ whiteSpace: 'pre-wrap' }}
      />
      <button onClick={handleUpdateResearch}>Update Research Projects</button>
    </div>
  );
}

// --- Updated Editor Components ---
function CertificationsEditor({ resumeHtml, editedData, setEditedData, setResumeHtml }) {
  const certifications = editedData.certifications || (resumeData && resumeData.certifications) || [];

  return (
    <div className="certifications-editor">
      <h3>Edit Certifications</h3>
      {certifications.map((cert, idx) => (
        <div key={idx} style={{ border: '1px solid #ccc', padding: '10px', marginBottom: '10px' }}>
          <label>Certification Name</label>
          <input
            type="text"
            value={cert.certificationName}
            onChange={(e) => {
              const newCert = { ...cert, certificationName: e.target.value };
              const updated = [...certifications];
              updated[idx] = newCert;
              setEditedData(prev => ({ ...prev, certifications: updated }));
            }}
          />
          <label>Issuing Organization</label>
          <input
            type="text"
            value={cert.issuingOrganization}
            onChange={(e) => {
              const newCert = { ...cert, issuingOrganization: e.target.value };
              const updated = [...certifications];
              updated[idx] = newCert;
              setEditedData(prev => ({ ...prev, certifications: updated }));
            }}
          />
          <label>Issue Date</label>
          <input
            type="text"
            value={cert.issueDate}
            onChange={(e) => {
              const newCert = { ...cert, issueDate: e.target.value };
              const updated = [...certifications];
              updated[idx] = newCert;
              setEditedData(prev => ({ ...prev, certifications: updated }));
            }}
          />
          <label>Expiration Date</label>
          <input
            type="text"
            value={cert.expirationDate}
            onChange={(e) => {
              const newCert = { ...cert, expirationDate: e.target.value };
              const updated = [...certifications];
              updated[idx] = newCert;
              setEditedData(prev => ({ ...prev, certifications: updated }));
            }}
          />
          {certifications.length > 1 && (
            <button onClick={() => {
              saveToHistory();
              const updated = certifications.filter((_, index) => index !== idx);
              setEditedData(prev => ({ ...prev, certifications: updated }));
            }}>
              Delete Certification
            </button>
          )}
        </div>
      ))}
      <button onClick={() => {
        saveToHistory();
        const newEntry = { certificationName: "", issuingOrganization: "", issueDate: "", expirationDate: "" };
        const updated = [...certifications, newEntry];
        setEditedData(prev => ({ ...prev, certifications: updated }));
      }}>
        Add New Certification
      </button>
      <button onClick={() => {
        saveToHistory();
        // Just call the function with resumeHtml (the string)
        const updatedHtml = updateCertificationsSection(
          resumeHtml, // <-- pass string
          editedData.certifications || certifications
        );
        setResumeHtml(updatedHtml);
      }}>
        Update Certifications
      </button>
    </div>
  );
}



function renderSummaryEditor() {
  // If you want to store newText in the parent:
  const summaryText = editedData.summary || (resumeData && resumeData.summary) || "";
  const handleTextChange = (e) => {
    setEditedData(prev => ({ ...prev, summary: e.target.value }));
  };

  const handleUpdate = () => {
    saveToHistory();
    const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
    updateSimpleStringSection(doc, "summary", summaryText);
    setResumeHtml(doc.documentElement.outerHTML);
  };

  return (
    <div>
      <h3>Edit Summary</h3>
      <textarea
        value={summaryText}
        onChange={handleTextChange}
        style={{ whiteSpace: 'pre-wrap' }}
      />
      <button onClick={handleUpdate}>Update Summary</button>
    </div>
  );
}



  // ----------------- PDF PREVIEW & DOWNLOAD -----------------
  const generatePdfBlob = () => {
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = resumeHtml;
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.width = '800px';
    document.body.appendChild(tempContainer);

    setTimeout(() => {
      const scaleFactor = 2;
      html2canvas(tempContainer, { scale: scaleFactor }).then((fullCanvas) => {
        const pdf = new jsPDF('p', 'pt', 'a4');
        const pdfWidth = pdf.internal.pageSize.getWidth();
        const pdfHeight = pdf.internal.pageSize.getHeight();

        const canvasWidth = fullCanvas.width;
        const canvasHeight = fullCanvas.height;
        const pageCanvasHeight = (pdfHeight * canvasWidth) / pdfWidth;

        let yPosition = 0;
        let pageCount = 0;
        let previewImagesArr = [];

        while (yPosition < canvasHeight) {
          pageCount++;
          let nextPageBoundary = yPosition + pageCanvasHeight;
          if (nextPageBoundary > canvasHeight) {
            nextPageBoundary = canvasHeight;
          }

          const pageCanvas = document.createElement('canvas');
          pageCanvas.width = canvasWidth;
          pageCanvas.height = nextPageBoundary - yPosition;

          const pageCtx = pageCanvas.getContext('2d');
          pageCtx.drawImage(
            fullCanvas,
            0,
            yPosition,
            canvasWidth,
            nextPageBoundary - yPosition,
            0,
            0,
            canvasWidth,
            nextPageBoundary - yPosition
          );

          const pageData = pageCanvas.toDataURL('image/png');
          previewImagesArr.push(pageData);

          yPosition = nextPageBoundary;
        }

        document.body.removeChild(tempContainer);
        setPdfPreviewUrl(null);
        setPreviewImages(previewImagesArr);
        setShowPreviewModal(true);
      });
    }, 500);
  };

  // Print handler: if user hasn’t paid and isn’t subscribed, show the payment modal; otherwise print
  const handlePrint = async () => {
    const auth = getAuth();
    const u = auth.currentUser;

    if (!u) {
      // no user → prompt them to sign in first
      // e.g. open your login modal or navigate
      return;
    }

    try {
      // Pass the uid into your helper and get back a boolean
      const active = await checkUserSubscription(u.uid);
      setIsSubscribed(active);

      if (active || hasPaid) {
        // they can print
        navigate('/print', { state: { resumeHtml } });
      } else {
        // no subscription or one‑time payment → show Stripe modal
        setShowPaymentModal(true);
      }
    } catch (err) {
      console.error('Error checking subscription status:', err);
      // fallback to payment modal
      setShowPaymentModal(true);
    }
  };

  const onClose = () => {
    setShowPaymentModal(false);
  };
  

  // Called when the payment modal successfully confirms payment
  const handlePaymentSuccess = () => {
    setHasPaid(true);
    setShowPaymentModal(false);
    // Generate a random token
    const randomToken = Math.random().toString(36).substr(2, 10); // 10-character token
    // Set expiration timestamp 10 minutes (10 * 60 * 1000 ms) from now.
    const expiresAt = Date.now() + 10 * 60 * 1000;
    // Save both the resume HTML and a token object as JSON in localStorage
    localStorage.setItem("resumeHtml", resumeHtml);
    localStorage.setItem("paidToken", JSON.stringify({ token: randomToken, expires: expiresAt }));
    // Navigate to print page; the PrintPreview page will check for the valid token.
    navigate('/print', { state: { resumeHtml } });
  };
  
  

  // When subscription succeeds, navigate to the print page.
  const handleSubscriptionSuccess = () => {
    // Generate a random 10-character token:
    const randomSubToken = Math.random().toString(36).substr(2, 10);
    // Set expiration timestamp: 10 minutes (10*60*1000 milliseconds) from now.
    const subExpiresAt = Date.now() + 10 * 60 * 1000;
    // Save the token object as JSON in localStorage under "subToken"
    localStorage.setItem("subToken", JSON.stringify({ token: randomSubToken, expires: subExpiresAt }));
    
    // Navigate to the print page; on PrintPreview we'll check for the valid token.
    navigate('/print', { state: { resumeHtml } });
  };


  // onRequireSignup simply opens the signup modal. 
  // The Signup component should be configured to take a callback or flag 
  // (e.g., a prop) so that once signup is complete, it triggers the subscription checkout.
  const handleRequireSignup = () => {
    // For example, if you use a state variable (or context) to control the signup modal:
    setIsSignupOpen(true);
  };

  // In your parent component (e.g., ResumeEditor2.js)
  const handleSignupComplete = async () => {
    setIsSignupOpen(false);
    // Now that the user is signed in, initiate the subscription checkout.
    try {
      await handleCheckout(); // This creates the Stripe Checkout session.
      // Do not immediately navigate here—Stripe will redirect via success_url.
    } catch (error) {
      console.error("Error during subscription checkout:", error);
    }
  };


  // ----------------- STATE HANDLERS -----------------
  const handleContactInfoChange = (field, value) => {
    setEditedData(prevData => ({
      ...prevData,
      contact_information: {
        ...(prevData.contact_information || {}),
        [field]: value
      }
    }));
  };

  const handleEditChange = (section, value) => {
    setEditedData(prevData => ({
      ...prevData,
      [section]: value
    }));
  };

  // ----------------- OTHER FUNCTIONS (add, remove, move) -----------------
  const addSectionToSidebar = (sectionId) => {
    saveToHistory();
    const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
    let sectionEl = doc.querySelector(`#${sectionId}`);
    if (!sectionEl) {
      sectionEl = doc.createElement("section");
      sectionEl.id = sectionId;
      sectionEl.innerHTML = `<h3>${sectionId.replace(/_/g, ' ')}</h3>`;
    }
    const sidebarEl = doc.querySelector("#sidebar");
    if (!sidebarEl) {
      console.warn("No #sidebar found in this template");
      return;
    }
    sidebarEl.appendChild(sectionEl);
    setResumeHtml(doc.documentElement.outerHTML);
  };

  // Helper function: Checks if a given section is already in the sidebar.
const sectionInSidebar = (sectionId) => {
  const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
  // Look only within the #sidebar container
  return Boolean(doc.querySelector(`#sidebar #${sectionId}`));
};

function sectionInContent(resumeHtml, sectionId) {
  const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
  const contentEl = doc.querySelector("#content");
  if (!contentEl) return false;
  // Check if there's an element with id=sectionId inside #content
  return Boolean(contentEl.querySelector(`#${sectionId}`));
}


  const removeSection = (sectionId) => {
    saveToHistory();
    const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
    const sectionEl = doc.querySelector(`#${sectionId}`);
    if (sectionEl) {
      sectionEl.remove();
    }
    setResumeHtml(doc.documentElement.outerHTML);
  };

  // Helper function inside your component (or imported)
  const sectionExists = (sectionId) => {
    const doc = new DOMParser().parseFromString(resumeHtml, "text/html");
    return Boolean(doc.querySelector(`#${sectionId}`));
  };


  // ----------------- RENDERING -----------------
  return (
    <>
      <div className="download-button-container">
        <button className="download-button" onClick={handlePrint}>
          Print Resume
        </button>
      </div>

      {/* Render payment modals together when showPaymentModal is true */}
      {showPaymentModal && (
        <div className="combined-payment-modal-overlay">
          <div className="combined-modal-container">
          <button className="modal-close container-close" onClick={onClose}>×</button>
            <OneTimePaymentModal
              isOpen={true}  // already open since showPaymentModal is true
              onClose={() => setShowPaymentModal(false)}
              onPaymentSuccess={handlePaymentSuccess}
            />
            <SubscriptionModal
              isOpen={true}
              onClose={() => setShowPaymentModal(false)}
              onSubscriptionSuccess={handleSubscriptionSuccess}
              onRequireSignup={handleRequireSignup}
              productType={"builder"}
            />
          </div>
        </div>
      )}

        {/* Toolbar positioned above the resume preview */}
      <div className="toolbar">
        <label>
          Font : 
          <select value={selectedFont} onChange={(e) => handleUpdateFont(e.target.value)}>
            <option value="Arial">Arial</option>
            <option value="Times New Roman">Times New Roman</option>
            <option value="Verdana">Verdana</option>
            <option value="Georgia">Georgia</option>
          </select>
        </label>
        <label>
          Size : 
          <select value={fontSize} onChange={(e) => handleUpdateFontSize(e.target.value)}>
            <option value="14px">14px</option>
            <option value="16px">16px</option>
            <option value="18px">18px</option>
            <option value="20px">20px</option>
          </select>
        </label>
        <label>
          Theme : 
          <input
            type="color"
            value={themeColor}
            onChange={(e) => handleUpdateThemeColor(e.target.value)}
          />
        </label>
        <button onClick={undoLastChange}>Undo</button>
      </div>
      <div className="resume-editor-layout">
        <div className="editor-sidebar">
          <div className="section-editor">
            <h3>Edit Sections</h3>
            <div className="section-list">
              {AI_SECTIONS.map(section => (
                <div key={section} className="section-item">
                  <button
                    onClick={() =>
                      setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }))
                    }
                    className="section-toggle"
                  >
                    {section.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase())}
                  </button>
                  {expandedSections[section] && (
                    <div>
                      {section === "contact_information" && (
                        <div>
                          {renderContactInfoEditor()}
                          {templateId === 5 && (
                            <ProfileImageUploader 
                              resumeHtml={resumeHtml}
                              setResumeHtml={setResumeHtml}
                              setEditedData={setEditedData}
                              saveToHistory={saveToHistory}
                            />
                          )}
                        </div>
                      )}
                      {section === "summary" && renderSummaryEditor()}
                      {section === "work_experience" && renderWorkExperienceEditor()}
                      {section === "education" && renderEducationEditor()}
                      {section === "skills" && (
                      <SkillsEditor
                      resumeHtml={resumeHtml}
                      resumeData={resumeData}  // Pass the fallback data here
                      editedData={editedData}
                      setEditedData={setEditedData}
                      setResumeHtml={setResumeHtml}
                      templateId={templateId}
                      saveToHistory={saveToHistory}
                    />
                    )}
                    {section === "hobbies" && (
                      <HobbiesEditor
                        resumeHtml={resumeHtml}
                        editedData={editedData}
                        setEditedData={setEditedData}
                        setResumeHtml={setResumeHtml}
                      />
                    )}
                    {section === "awards_and_achievements" && (
                      <AwardsEditor
                        resumeHtml={resumeHtml}
                        editedData={editedData}
                        setEditedData={setEditedData}
                        setResumeHtml={setResumeHtml}
                      />
                    )}
                    {section === "research_projects" && (
                      <ResearchProjectsEditor
                        resumeHtml={resumeHtml}
                        editedData={editedData}
                        setEditedData={setEditedData}
                        setResumeHtml={setResumeHtml}
                      />
                    )}
                    {section === "presentations" && (
                      <PresentationsEditor
                        resumeHtml={resumeHtml}
                        editedData={editedData}
                        setEditedData={setEditedData}
                        setResumeHtml={setResumeHtml}
                      />
                    )}
                    {section === "languages" && (
                      <LanguagesEditor
                        resumeHtml={resumeHtml}
                        editedData={editedData}
                        setEditedData={setEditedData}
                        setResumeHtml={setResumeHtml}
                      />
                    )}
                    {section === "certifications" && (
                      <CertificationsEditor
                        setResumeHtml={setResumeHtml}
                        resumeHtml={resumeHtml}
                        editedData={editedData}
                        resumeData={resumeData}
                        setEditedData={setEditedData}        // ensure you pass this
                        saveToHistory={saveToHistory}  // ensure you pass this
                      />
                    )}
                    {section === "projects" && (
                      <ProjectsEditor
                        resumeHtml={resumeHtml}
                        editedData={editedData}
                        setEditedData={setEditedData}
                        setResumeHtml={setResumeHtml}
                        resumeData={resumeData}           // pass resumeData here
                        saveToHistory={saveToHistory}     // pass saveToHistory here
                      />
                    )}
                    {/* Conditionally render Add to Sidebar button:
                    - Only if there is a sidebar (hasSidebar === true)
                    - And the current section is included in sidebarSections
                    - And the section is not already present in the sidebar */}
                    {/* Conditionally render Add to Sidebar button */}
                    {hasSidebar &&
                    sectionInContent(resumeHtml, section) &&
                    !sectionInSidebar(section) && sidebarSections.includes(section) && (
                      <button onClick={() => addSectionToSidebar(section)}>
                        Add to Sidebar
                      </button>
                    )}
                    {!mainSections.includes(section) && sectionExists(section) && (
                      <button onClick={() => removeSection(section)}>
                        Remove
                      </button>
                    )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="resume-preview">
          <iframe ref={iframeRef} title="Resume Preview" />
        </div>
      </div>
      {showPreviewModal && (
        <div 
          className="pdf-modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(5px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2000
          }}
          onContextMenu={(e) => e.preventDefault()}
        >
          <div 
            className="pdf-modal-content" 
            style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              width: '850px',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative'
            }}
          >
            <h3>PDF Preview</h3>
            {previewImages.map((imgSrc, idx) => (
              <div 
                key={idx}
                style={{
                  position: 'relative',
                  marginBottom: '20px'
                }}
                onContextMenu={(e) => e.preventDefault()}
              >
                <img 
                  src={imgSrc} 
                  alt={`Preview Page ${idx + 1}`} 
                  style={{ width: '100%', display: 'block' }} 
                  onContextMenu={(e) => e.preventDefault()}
                />
                <div 
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%) rotate(-45deg)',
                    fontSize: '8em',
                    color: 'rgba(0, 0, 0, 0.15)',
                    pointerEvents: 'none',
                    userSelect: 'none',
                    width: '100%',
                    textAlign: 'center',
                    whiteSpace: 'nowrap'
                  }}
                >
                  PositionPrep
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

export default ResumeEditor;
